import { Award, Download, ExternalLink } from "lucide-react";

const CERTIFICATES = [
  {
    id: "who-gmp",
    title: "WHO-GMP Certificate",
    issuer: "UKAS — United Kingdom Accreditation Service",
    description:
      "Certified to manufacture pharmaceutical-grade veterinary formulations under World Health Organization Good Manufacturing Practices.",
    pdf_url: "/certs/who-gmp.pdf",
    preview: "/certs/who-gmp.png",
    badge: "WHO-GMP",
  },
  {
    id: "iso-9001",
    title: "ISO 9001:2015 Certificate",
    issuer: "International Organization for Standardization",
    description:
      "Quality Management System certification covering manufacturing, R&D, packaging and customer service processes.",
    pdf_url: "/certs/iso-9001.pdf",
    preview: "/certs/iso-9001.png",
    badge: "ISO 9001:2015",
  },
];

export default function QualityCertificates() {
  return (
    <section
      id="certificates"
      className="section-padding bg-white"
      data-testid="quality-certificates"
    >
      <div className="container-fr">
        <div className="max-w-3xl mb-12">
          <span className="overline">Our Certifications</span>
          <h2 className="h-section mt-3">Audited. Accredited. Documented.</h2>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Our manufacturing systems and quality processes are externally
            audited and certified. View or download our latest certificates
            below.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {CERTIFICATES.map((c, i) => (
            <article
              key={c.id}
              data-testid={`quality-cert-${c.id}`}
              className="bg-[#F8FAFC] border border-slate-200 rounded-xl overflow-hidden flex flex-col card-lift fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              {/* Certificate preview image (first page) */}
              <a
                href={c.pdf_url}
                target="_blank"
                rel="noreferrer"
                className="relative block bg-white border-b border-slate-200 group"
                data-testid={`open-cert-${c.id}`}
              >
                <img
                  src={c.preview}
                  alt={`${c.title} preview`}
                  loading="lazy"
                  className="w-full h-[520px] object-contain object-top bg-slate-50"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0B2545] text-white px-3 py-1.5 rounded-md shadow-md">
                  <Award size={14} className="text-[#C8A14B]" />
                  <span className="text-xs font-semibold tracking-wider">
                    {c.badge}
                  </span>
                </div>
                <div className="absolute inset-0 bg-[#0B2545]/0 group-hover:bg-[#0B2545]/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="bg-white text-[#0B2545] px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 shadow-lg">
                    <ExternalLink size={14} /> Open full certificate
                  </span>
                </div>
              </a>

              <div className="p-7 flex flex-col flex-grow">
                <h3 className="font-display text-2xl font-bold text-[#0B2545]">
                  {c.title}
                </h3>
                <div className="text-xs uppercase tracking-wider text-[#2A9D8F] font-semibold mt-2">
                  Issued by {c.issuer}
                </div>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed">
                  {c.description}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={c.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`view-cert-${c.id}`}
                    className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#13345F] text-white px-5 py-2.5 rounded-md text-sm font-medium transition-all hover:-translate-y-0.5"
                  >
                    <ExternalLink size={14} /> View Full Certificate
                  </a>
                  <a
                    href={c.pdf_url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`download-cert-${c.id}`}
                    className="inline-flex items-center gap-2 border-2 border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white px-5 py-2.5 rounded-md text-sm font-medium transition-all"
                  >
                    <Download size={14} /> Download PDF
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
